import { PageLayout, DriverInfo } from '@/components';
import { OrderComments, ProofOfDeliveryCard } from '@/components/OrderDetailsCards';
import { RemittanceCompletedToast } from '@/components/OrderDetailsCards/RemittanceCompletedToast';
import { ResolveModal } from '@/components/ResolveModal';
import {
  RotateCw,
  Ticket,
  Truck,
  MoreHorizontal,
  Pencil,
  FileDown,
  FileText,
  Download,
  X,
  Copy,
  MessageCircle,
} from 'lucide-react';
import { useResendStore } from '@/stores/useResendStore';
import { InventoryOrder } from '@/types/inventory';
import DelayRtoConfirmModal from '@/components/Order/DelayRtoConfirmModal';
import ScheduleReattemptButton from '@/components/Order/ScheduleReattemptButton';
import { PackageSizeEnum, PackageSizeFormValues } from '@/components/OrderForm/PackageSizeCard';
import { PackagesSection } from './components/PackagesSection';
import { OrderStatusEnum, ROUTE, StatusesEnum } from '@/config';
import {
  useHandleCancelOrder,
  useLoadMorePagination,
  useOpenEntityTicketModal,
  useResolveModalStore,
} from '@/hooks';
import useOrderTimeline from '@/hooks/useOrderTimeline ';
import useSubmitSingleOrder from '@/hooks/useSubmitSingleOrder';
import { useFetchStatusAnnotationsByOrderUID, useDuplicateOrder } from '@/hooksApi/useOrder';
import { useQueryClient } from '@tanstack/react-query';
import { fetchOrderActivityLogByOrderUID } from '@/api/orderService';
import useLanguageStore from '@/stores/useLanguageStore';
import { OrderTypeEnum } from '@/stores/useOrderTypeStore';
import {
  ActivityItem,
  OrderItem,
  OrderStatusTab,
  PaymentType,
  ShipmentFeeType,
  PermissionName,
} from '@/types';
import {
  exportOrders,
  formatDateByDayjs,
  formatWithThousandSeparator,
  getOrderOptions,
  getOrderPickupAddress,
  getOrderStatusTagData,
  getOrderTypeTextKey,
  getOrderTypeLabelKey,
  translateCarrierMessage,
} from '@/utils';
import { message, Dropdown } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import TransactionListSection from './TransactionListSection';
import { TicketEntityIDFieldEnum } from '@/constants';
import usePermission from '@/hooks/usePermission';
import useOrdersPaused from '@/hooks/useOrdersPaused';
import useOrderUpdatesEnabled from '@/hooks/useOrderUpdatesEnabled';
import useUserStore, { selectUsesServices } from '@/stores/userStore';
import { ShieldAlert, ChevronLeft } from 'lucide-react';
import ClaimStatusTag from '@/components/claims/ClaimStatusTag';
import FileClaimModal from '@/components/claims/FileClaimModal';

// New redesigned components
import { OrderDetailsHeader } from './components/OrderDetailsHeader';
import { OrderPageHeader } from './components/OrderPageHeader';
// ExceptionBanner import kept off — standalone banner replaced by the unified
// banner below (see Bug #1 round 3). Component file is retained for reuse.
import { RemittanceBanner } from './components/RemittanceBanner';
import { CarrierSection } from './components/CarrierSection';
import { OrderInfoSection } from './components/OrderInfoSection';
import { CustomerSection } from './components/CustomerSection';
import { ItemSection } from './components/ItemSection';
import { PickupSection } from './components/PickupSection';
import { PackageSection } from './components/PackageSection';
import { CostSummarySection } from './components/CostSummarySection';
import { costAmounts } from '@/components/CostBreakdown/costAmounts';
import { PaymentSummarySection } from './components/PaymentSummarySection';
import { TrackingTimeline } from './components/TrackingTimeline';
import { ActivityLogSection } from './components/ActivityLogSection';
import { OrderDetailsSkeleton } from './components/OrderDetailsSkeleton';
import { TicketsSection } from './components/TicketsSection';
import { OrderUpdatesTab } from './components/updates/OrderUpdatesTab';
import { headerShowsResolve } from '@/utils/orderResolve';
import { RtoNoteCard } from '@/components/Order/RtoNote/RtoNoteCard';
import { AmountRequestCard } from '@/components/Order/AmountRequest/AmountRequestCard';
import { PartiallyDeliveredBadge } from '@/components/PartiallyDelivered/PartiallyDeliveredBadge';
import usePartialDeliveryLabel from '@/hooks/usePartialDeliveryLabel';

const OrderDetails = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const { language } = useLanguageStore();
  const { hasPermission } = usePermission();
  const ordersPaused = useOrdersPaused();
  const updatesTabEnabled = useOrderUpdatesEnabled();
  const usesServices = useUserStore(selectUsesServices);

  const leftSectionRef = useRef<HTMLDivElement>(null);

  const [leftSectionHeight, setLeftSectionHeight] = useState<number | undefined>(undefined);
  const [shouldCrash, setShouldCrash] = useState(false);
  const [activeSegment, setActiveSegment] = useState<'details' | 'updates'>('details');
  const [resolveModalOpen, setResolveModalOpen] = useState(false);
  const [claimModalOpen, setClaimModalOpen] = useState(false);
  const [delayRtoModalOpen, setDelayRtoModalOpen] = useState(false);
  const [delayRtoTargetOrders, setDelayRtoTargetOrders] = useState<InventoryOrder[]>([]);

  const { setSelectedOrders } = useResendStore();

  // Zustand store setters
  const setFullName = useResolveModalStore((state) => state.setFullName);
  const setPlatformCode = useResolveModalStore((state) => state.setPlatformCode);
  const setPhone = useResolveModalStore((state) => state.setPhone);
  const setOrderValue = useResolveModalStore((state) => state.setOrderValue);
  const setAddressTitle = useResolveModalStore((state) => state.setAddressTitle);

  const { timelineItems, isLoading, data } = useOrderTimeline(orderId as string);

  // Cold storage: an order older than the backend's hot window keeps its
  // history in Mongo, and the merchant asks for it explicitly. The flag comes
  // from the API rather than being derived from created_at here, so the window
  // stays one value in one place.
  const [activityLogRequested, setActivityLogRequested] = useState(false);
  const activityLogArchived = Boolean(data?.object?.activity_log_archived);

  const activityLogFetcher = useCallback(
    (page: number) => {
      // Flipping activityLogRequested changes this callback's identity, which
      // is exactly what makes useLoadMorePagination run the real request — no
      // change to the shared hook needed.
      if (activityLogArchived && !activityLogRequested) return Promise.resolve([]);

      return fetchOrderActivityLogByOrderUID(orderId as string, page, 200).then(
        (res) => res.object.items,
      );
    },
    [orderId, language, activityLogArchived, activityLogRequested],
  );

  const {
    items: activityLogItems,
    isLoading: loadingActivityLog,
    hasMore: hasMoreActivity,
    loadingMore: loadingMoreActivity,
    loadMore: loadMoreActivity,
  } = useLoadMorePagination<ActivityItem>(activityLogFetcher);
  const { data: statusAnnotationsData } = useFetchStatusAnnotationsByOrderUID(orderId || '');

  const response = data?.object;
  const partialDeliveryLabel = usePartialDeliveryLabel(response?.partial_delivery);
  // 🔴 THE SELECT SUPPRESSION IS GONE FROM THE CLIENT ON PURPOSE (owner
  // request, 2026-09-03 — merchants match the carrier's reason against
  // Boxy's, and a held parcel with no reason tells them nothing).
  //
  // The disclosure rule now lives only in the API, which already applies it
  // for the webhook (`Order::reasonForMerchant()`, owner decision
  // 2026-08-13: the carrier's text goes out on the HOLD statuses, Select or
  // not, and stays scrubbed everywhere else). A second copy of that rule
  // here could only ever disagree with it — and this copy cannot see the
  // status policy at all. If the API sends the text, the merchant may read
  // it; if it scrubs it, there is nothing to render.
  // BOXY-587 — an internal carrier code ("'RETURN_APPROVED' is not allowed…")
  // translates to '' and hides the block rather than reaching the merchant.
  const carrierMessage = translateCarrierMessage(response?.carrier_message, t) || undefined;

  const orderOptions = getOrderOptions(response?.options);

  // Hand the loaded order to the ResolveModal store.
  //
  // R54 (CA.1) — this ran in the RENDER BODY, so every render of this page
  // wrote five values into a store that ResolveModal subscribes to, which
  // React reports as "Cannot update a component while rendering a different
  // component". It is not cosmetic: an update scheduled mid-render is applied
  // outside React's normal flow, and with a sibling subscriber the two
  // components can read different values within the same commit. In an effect
  // the write lands after the commit, once, when the response actually
  // changes.
  useEffect(() => {
    if (!response) return;

    setFullName(response.full_name || '');
    setPlatformCode(response?.platform_code || '');
    setPhone(response.phone || '');
    setOrderValue(String(response.preview?.orders_value || 0));
    setAddressTitle(getOrderPickupAddress(response as unknown as OrderItem)?.title || '');
  }, [response, setFullName, setPlatformCode, setPhone, setOrderValue, setAddressTitle]);

  const activeTab = getOrderStatusTagData(response?.status?.slug as OrderStatusEnum)
    ?.key as OrderStatusTab;

  const { onSubmit, LoadingRequestPickup } = useSubmitSingleOrder({
    activeTab,
    orderId: orderId as string,
    pickupId: getOrderPickupAddress(response as unknown as OrderItem)?.uid,
    statusSlug: response?.status?.slug,
  });

  const { handleCancelOrder, isLoading: cancelLoading } = useHandleCancelOrder(
    orderId || '',
    activeTab,
    response?.status?.slug,
  );
  const { openEntityTicketModal } = useOpenEntityTicketModal(TicketEntityIDFieldEnum.ORDER);
  const { mutate: duplicateOrder } = useDuplicateOrder();
  const queryClient = useQueryClient();

  const handleCopy = () => {
    if (response?.platform_code) {
      navigator.clipboard.writeText(response.platform_code);
      message.success(t('copy'));
    }
  };

  const handleExport = async () => {
    if (response?.uid) {
      const queryString = `filter_uids[]=${encodeURIComponent(response.uid)}`;
      await exportOrders(queryString, 'order');
    }
  };

  useEffect(() => {
    if (leftSectionRef.current) {
      const leftSectionHeight =
        leftSectionRef.current.offsetHeight +
        leftSectionRef.current.getBoundingClientRect().top +
        window.scrollY;
      setLeftSectionHeight(leftSectionHeight);
    }
  }, [response, statusAnnotationsData, leftSectionRef?.current?.offsetHeight]);

  // Edit is allowed: new, scheduled, and on-hold (when NOT hold-resolved/exception)
  const statusSlugForActions = response?.status?.slug || '';
  const editAllowedStatuses = ['new', 'scheduled'];
  const editAllowedOnHold = !['hold-resolved', 'on-hold', 'exception'].includes(
    statusSlugForActions,
  );
  const editDisabled =
    !hasPermission(PermissionName.UPDATE_ORDER) ||
    !(
      editAllowedStatuses.includes(statusSlugForActions) ||
      (activeTab === OrderStatusTab.ON_HOLD && editAllowedOnHold)
    );

  // Cancel/Delete is allowed: new, scheduled, collecting, collected
  // + on-hold when NOT hold-resolved/exception
  const cancelAllowedStatuses = ['new', 'scheduled', 'collecting', 'collected'];
  const cancelDisabled =
    !hasPermission(PermissionName.DELETE_ORDER) ||
    !(
      cancelAllowedStatuses.includes(statusSlugForActions) ||
      (activeTab === OrderStatusTab.ON_HOLD && editAllowedOnHold)
    );

  // if (shouldCrash) {
  //   throw new Error('Test Error - This should trigger ErrorBoundary');
  // }

  return (
    <PageLayout>
      <div className='relative flex flex-col h-full'>
        {/* No inner header bar — Main layout provides the header.
            CTA buttons are rendered in the sub-header via OrderPageHeader actions prop. */}

        {/* Scrollable Body */}
        <div className='flex-1 overflow-y-auto px-4 sm:px-6 pt-5 pb-8'>
          {/* Loading / error / content states */}
          {isLoading ? (
            <OrderDetailsSkeleton />
          ) : !response ? (
            <div className='flex flex-col items-center justify-center py-20 text-center'>
              <p className='text-sd-muted-foreground text-sm'>{String(t('order_not_found'))}</p>
              <button
                onClick={() => navigate(-1)}
                className='mt-4 h-9 px-4 rounded-[0.50rem] border border-sd-border bg-sd-card text-[13px] font-semibold text-sd-foreground hover:bg-sd-muted transition-colors cursor-pointer'
              >
                {String(t('go_back'))}
              </button>
            </div>
          ) : (
            <>
              {/* Remittance completed banner removed — duplicate of RemittanceBanner below */}

              {/* Page Header: Order ID, date, status, tabs */}
              <OrderPageHeader
                platformCode={response.platform_code || ''}
                createdAt={formatDateByDayjs(response.created_at, 'D MMM YYYY, HH:mm')}
                statusSlug={response.status?.slug || ''}
                statusTitle={
                  response.is_bulk_collected && response.status?.slug === 'collected'
                    ? String(t('merchant_title_order_status.bulk-collected'))
                    : String(t(`merchant_title_order_status.${response.status?.slug}`))
                }
                orderTypeBadge={
                  response.type !== 'DEFAULT'
                    ? String(t(getOrderTypeLabelKey(response.type as OrderTypeEnum)))
                    : undefined
                }
                activeSegment={activeSegment}
                onSegmentChange={setActiveSegment}
                showUpdatesTab={updatesTabEnabled}
                onCopyOrderId={handleCopy}
                statusNote={
                  partialDeliveryLabel ? (
                    <PartiallyDeliveredBadge label={partialDeliveryLabel} />
                  ) : null
                }
                actions={(() => {
                  const slug = response.status?.slug || '';
                  const isNew = slug === 'new';
                  const isScheduled = slug === 'scheduled';
                  const isInProgress = [
                    'collecting',
                    'collected',
                    'en-route',
                    'out-for-delivery',
                    'out-for-collection',
                    'postponed',
                  ].includes(slug);
                  const isInventory = slug === 'inventory';

                  const outlineBtn =
                    'flex items-center gap-1.5 h-8 px-3 rounded-lg border border-solid border-[rgb(232,230,227)] bg-white text-[11px] font-semibold text-[rgb(57,57,60)] cursor-pointer hover:bg-[rgb(245,245,243)] transition-colors';
                  const primaryBtn =
                    'flex items-center gap-1.5 h-8 px-3.5 rounded-lg bg-[rgb(237,224,74)] text-[rgb(57,57,60)] text-[11px] font-semibold cursor-pointer hover:bg-[rgb(230,217,60)] transition-colors disabled:opacity-50';

                  const ticketBtn = hasPermission(PermissionName.CREATE_TICKET) ? (
                    <button
                      className={outlineBtn}
                      onClick={() => openEntityTicketModal(response?.platform_code, response?.uid)}
                    >
                      <Ticket size={13} /> {String(t('ticket'))}
                    </button>
                  ) : null;

                  // Compute label button text: "Reprint" if already printed, else "Download Label"
                  const labelBtnText = response?.label_printed ? t('reprint') : t('download_label');

                  // Build kebab menu items with icons
                  const kebabItems = [
                    ...(!editDisabled
                      ? [
                          {
                            key: 'edit',
                            label: (
                              <span className='flex items-center gap-2 text-[13px]'>
                                <Pencil size={14} className='text-[rgb(115,123,140)]' />
                                {String(t('edit'))}
                              </span>
                            ),
                            onClick: () =>
                              navigate(generatePath(ROUTE.ORDER_EDIT, { orderUid: orderId })),
                          },
                        ]
                      : []),
                    ...(!isInventory && !isNew && !isScheduled && !isInProgress
                      ? [
                          {
                            key: 'download_label',
                            label: (
                              <span className='flex items-center gap-2 text-[13px]'>
                                <FileDown size={14} className='text-[rgb(115,123,140)]' />
                                {String(labelBtnText)}
                              </span>
                            ),
                            onClick: onSubmit,
                          },
                        ]
                      : []),
                    ...(!isInventory
                      ? [
                          {
                            key: 'export',
                            label: (
                              <span className='flex items-center gap-2 text-[13px]'>
                                <FileText size={14} className='text-[rgb(115,123,140)]' />
                                {String(t('export'))}
                              </span>
                            ),
                            onClick: handleExport,
                          },
                        ]
                      : []),
                    ...(hasPermission(PermissionName.CREATE_ORDER) && !ordersPaused
                      ? [
                          {
                            key: 'duplicate',
                            label: (
                              <span className='flex items-center gap-2 text-[13px]'>
                                <Copy size={14} className='text-[rgb(115,123,140)]' />
                                {String(t('duplicate_order'))}
                              </span>
                            ),
                            onClick: () => {
                              if (!orderId) return;
                              duplicateOrder(orderId, {
                                onSuccess: () => {
                                  message.success(String(t('operation_completed_successfully')));
                                  queryClient.invalidateQueries(['orders']);
                                },
                              });
                            },
                          },
                        ]
                      : []),
                    ...(!cancelDisabled
                      ? [
                          {
                            key: 'cancel',
                            label: (
                              <span className='flex items-center gap-2 text-[13px] text-[rgb(239,68,68)]'>
                                <X size={14} />
                                {String(t(slug === 'new' ? 'delete' : 'cancel'))}
                              </span>
                            ),
                            onClick: async () => {
                              await handleCancelOrder();
                              if (slug === 'new') navigate(ROUTE.ORDERS);
                            },
                          },
                        ]
                      : []),
                  ];

                  return (
                    <>
                      {/* Status-dependent CTA buttons */}
                      {isInProgress && (
                        <button className={outlineBtn} onClick={onSubmit}>
                          <Download size={13} /> {String(labelBtnText)}
                        </button>
                      )}
                      {ticketBtn}
                      {isScheduled && (
                        <button className={outlineBtn} onClick={onSubmit}>
                          <Download size={13} /> {String(labelBtnText)}
                        </button>
                      )}
                      {isNew && (
                        <button
                          className={primaryBtn}
                          onClick={onSubmit}
                          disabled={LoadingRequestPickup || ordersPaused}
                        >
                          {LoadingRequestPickup ? (
                            <span className='h-3 w-3 rounded-full border-2 border-[rgb(57,57,60)] border-t-transparent animate-spin' />
                          ) : (
                            <Truck size={13} />
                          )}
                          {String(t('ship_now')).toUpperCase()}
                        </button>
                      )}
                      {headerShowsResolve(response) && (
                        <button className={primaryBtn} onClick={() => setResolveModalOpen(true)}>
                          {String(t('resolve')).toUpperCase()}
                        </button>
                      )}
                      {isInventory && (
                        <>
                          <button
                            className={primaryBtn}
                            onClick={() => {
                              const inventoryOrder: InventoryOrder = {
                                uid: response.uid,
                                platform_code: response.platform_code,
                                full_name: response.full_name || '',
                                phone: response.phone || '',
                                address_text: response.address_text || '',
                                region_id: 0,
                                city_id: 0,
                                region: response.region
                                  ? {
                                      uid: response.region.uid,
                                      province_code: response.region.province_code || '',
                                      province: response.region.province || '',
                                      province_en_name: '',
                                      area_name: response.region.area_name || '',
                                    }
                                  : undefined,
                                size: response.size || '',
                                type: response.type || '',
                                is_fragile: !!response.is_fragile,
                                fee: response.fee || 0,
                                products: (response.products || []).map((p: any) => ({
                                  title: p.title,
                                  price: p.price,
                                  quantity:
                                    typeof p.quantity === 'string'
                                      ? Number(p.quantity)
                                      : p.quantity,
                                })),
                                parent_order_id: null,
                                parent_order_uid: null,
                                recommended_carrier_slug: response.carrier?.slug || null,
                                available_carriers_detail: [],
                                created_at: response.created_at || '',
                                merchant_id: 0,
                              };
                              setSelectedOrders([inventoryOrder]);
                              navigate(ROUTE.RESEND);
                            }}
                          >
                            <RotateCw size={13} /> {String(t('resend')).toUpperCase()}
                          </button>
                          <button
                            className={outlineBtn}
                            onClick={() => {
                              const inventoryOrder: InventoryOrder = {
                                uid: response.uid,
                                platform_code: response.platform_code,
                                full_name: response.full_name || '',
                                phone: response.phone || '',
                                address_text: response.address_text || '',
                                region_id: 0,
                                city_id: 0,
                                region: response.region
                                  ? {
                                      uid: response.region.uid,
                                      province_code: response.region.province_code || '',
                                      province: response.region.province || '',
                                      province_en_name: '',
                                      area_name: response.region.area_name || '',
                                    }
                                  : undefined,
                                size: response.size || '',
                                type: response.type || '',
                                is_fragile: !!response.is_fragile,
                                fee: response.fee || 0,
                                products: (response.products || []).map((p: any) => ({
                                  title: p.title,
                                  price: p.price,
                                  quantity:
                                    typeof p.quantity === 'string'
                                      ? Number(p.quantity)
                                      : p.quantity,
                                })),
                                parent_order_id: null,
                                parent_order_uid: null,
                                recommended_carrier_slug: response.carrier?.slug || null,
                                available_carriers_detail: [],
                                created_at: response.created_at || '',
                                merchant_id: 0,
                              };
                              setDelayRtoTargetOrders([inventoryOrder]);
                              setDelayRtoModalOpen(true);
                            }}
                          >
                            {String(t('return_now')).toUpperCase()}
                          </button>
                        </>
                      )}
                      {['on-hold', 'postponed', 'inventory'].includes(slug) && (
                        <ScheduleReattemptButton
                          uid={response.uid}
                          statuses={response.statuses}
                          triggerClassName={outlineBtn}
                        />
                      )}
                      {/* Kebab menu with icons */}
                      <Dropdown
                        menu={{ items: kebabItems }}
                        trigger={['click']}
                        placement='bottomRight'
                      >
                        <button className='flex items-center justify-center w-8 h-8 rounded-lg border border-solid border-[rgb(232,230,227)] bg-white text-[rgb(115,123,140)] cursor-pointer hover:bg-[rgb(245,245,243)] transition-colors'>
                          <MoreHorizontal size={16} />
                        </button>
                      </Dropdown>
                    </>
                  );
                })()}
              />

              {/* Bug #1 (round 3): the standalone ExceptionBanner used to render
                  `merchant_action_required_text` for non-on-hold statuses. That
                  is the same value QA sees in the unified banner below under
                  "Boxy Comment" (the latest status annotation), so rendering
                  both produced duplicate "exception reason" copy on a single
                  order page. Removed in favour of the unified banner. */}

              {/* FE-1: Prompt merchants to add a note describing the products
                  being returned / exchanged. The description field on the
                  order serves as that note — show a dedicated amber banner
                  for RETURN/EXCHANGE types so the merchant notices the ask
                  before the driver arrives. If the description is already
                  filled, render it in the banner; otherwise surface an
                  "Edit order" CTA that opens the existing edit flow. */}
              {(response.type === OrderTypeEnum.RETURN ||
                response.type === OrderTypeEnum.EXCHANGE) && (
                <div
                  data-testid='return-exchange-note-banner'
                  className='rounded-2xl border border-solid mb-4 px-4 py-3.5 flex items-start gap-3'
                  style={{
                    backgroundColor: 'rgba(245,158,11,0.06)',
                    borderColor: 'rgba(245,158,11,0.25)',
                  }}
                >
                  <div
                    className='w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5'
                    style={{ backgroundColor: 'rgba(245,158,11,0.15)' }}
                  >
                    <MessageCircle className='w-3.5 h-3.5' style={{ color: 'rgb(180,115,20)' }} />
                  </div>
                  <div className='flex-1 min-w-0'>
                    <p
                      className='text-[11px] font-bold uppercase tracking-wider m-0 mb-0.5'
                      style={{ color: 'rgb(180,115,20)' }}
                    >
                      {response.type === OrderTypeEnum.RETURN
                        ? String(t('return_order_note_title', { defaultValue: 'Return details' }))
                        : String(
                            t('exchange_order_note_title', { defaultValue: 'Exchange details' }),
                          )}
                    </p>
                    {response.description ? (
                      <p className='text-[13px] m-0' style={{ color: 'rgb(100,70,10)' }}>
                        {response.description}
                      </p>
                    ) : (
                      <p className='text-[12px] m-0' style={{ color: 'rgb(120,85,20)' }}>
                        {String(
                          t('return_exchange_note_prompt', {
                            defaultValue:
                              'Add a note describing the products being returned or exchanged so the driver knows what to collect.',
                          }),
                        )}
                      </p>
                    )}
                  </div>
                  {!editDisabled && (
                    <button
                      type='button'
                      onClick={() => {
                        if (response.uid) {
                          navigate(generatePath(ROUTE.ORDER_EDIT, { uid: response.uid }));
                        }
                      }}
                      className='shrink-0 rounded-lg border border-solid px-3 py-1.5 text-[11px] font-semibold cursor-pointer'
                      style={{
                        borderColor: 'rgba(180,115,20,0.4)',
                        color: 'rgb(120,85,20)',
                        backgroundColor: 'rgba(245,158,11,0.08)',
                      }}
                    >
                      {response.description
                        ? String(t('edit'))
                        : String(t('add_note', { defaultValue: 'Add note' }))}
                    </button>
                  )}
                </div>
              )}

              {/* F1 (2nd pass): Always show Carrier Reason + Boxy Comment when they
                  exist, in EVERY status — including RTO. Merchants want the
                  banner on top regardless of current status; a separate red
                  RTO banner is no longer rendered (the RTO status badge at
                  the top of the page already marks the order as RTO, so the
                  dedicated red banner was duplicating the carrier message). */}
              {(carrierMessage || (statusAnnotationsData?.object?.items?.length ?? 0) > 0) && (
                <div
                  className='rounded-2xl border border-solid mb-4 overflow-hidden'
                  style={{ borderColor: 'rgba(59,130,246,0.2)' }}
                >
                  {carrierMessage && (
                    <div
                      className='flex items-start gap-3 px-4 py-3.5'
                      style={{ backgroundColor: 'rgba(59,130,246,0.04)' }}
                    >
                      <div
                        className='w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5'
                        style={{ backgroundColor: 'rgba(59,130,246,0.12)' }}
                      >
                        <Truck className='w-3.5 h-3.5' style={{ color: 'rgb(37,99,235)' }} />
                      </div>
                      <div className='flex-1 min-w-0'>
                        <p
                          className='text-[11px] font-bold uppercase tracking-wider m-0 mb-0.5'
                          style={{ color: 'rgb(37,99,235)' }}
                        >
                          {String(t('delivery_comment'))}
                        </p>
                        <p className='text-[13px] m-0' style={{ color: 'rgb(30,64,175)' }}>
                          {carrierMessage}
                        </p>
                        {response.updated_at && (
                          <p className='text-[11px] mt-1 m-0' style={{ color: 'rgb(96,130,210)' }}>
                            {formatDateByDayjs(response.updated_at, 'D MMM YYYY, HH:mm')}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                  {(statusAnnotationsData?.object?.items?.length ?? 0) > 0 &&
                    (() => {
                      const latest = statusAnnotationsData!.object!.items[0];
                      const tagTitle = latest.status_annotation_tag?.title;
                      const hasTag = typeof tagTitle === 'string' && tagTitle.trim().length > 0;
                      const hasText =
                        typeof latest.text === 'string' && latest.text.trim().length > 0;
                      if (!hasTag && !hasText) return null;
                      return (
                        <div
                          className='flex items-start gap-3 px-4 py-3.5'
                          style={{
                            backgroundColor: 'rgba(245,158,11,0.06)',
                            ...(carrierMessage
                              ? { borderTop: '1px solid rgba(232,230,227,0.6)' }
                              : {}),
                          }}
                        >
                          <div
                            className='w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5'
                            style={{ backgroundColor: 'rgba(245,158,11,0.15)' }}
                          >
                            <MessageCircle
                              className='w-3.5 h-3.5'
                              style={{ color: 'rgb(180,115,20)' }}
                            />
                          </div>
                          <div className='flex-1 min-w-0'>
                            <p
                              className='text-[11px] font-bold uppercase tracking-wider m-0 mb-0.5'
                              style={{ color: 'rgb(180,115,20)' }}
                            >
                              {String(t('boxy_comment'))}
                            </p>
                            {hasTag && (
                              <p
                                className='text-[13px] font-semibold m-0'
                                style={{ color: 'rgb(80,55,5)' }}
                              >
                                {tagTitle}
                              </p>
                            )}
                            {hasText && (
                              <p className='text-[13px] m-0' style={{ color: 'rgb(100,70,10)' }}>
                                {latest.text}
                              </p>
                            )}
                            {latest.created_at && (
                              <p
                                className='text-[11px] mt-1 m-0'
                                style={{ color: 'rgb(160,120,40)' }}
                              >
                                {formatDateByDayjs(latest.created_at, 'D MMM YYYY, HH:mm')}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                </div>
              )}

              {/* BOXY-376 — the return request note, kept after the order leaves on-hold. */}
              {response.rto_note ? <RtoNoteCard note={response.rto_note} /> : null}

              {/* BOXY-888 — the driver's amount change waits for the merchant's final amount. */}
              {response.amount_request ? (
                <AmountRequestCard orderUid={response.uid} request={response.amount_request} />
              ) : null}

              {/* Remittance Banner */}
              {response.remittance && (
                <RemittanceBanner
                  remittanceId={response.remittance.platform_code}
                  remittanceDate={formatDateByDayjs(response.remittance.created_at, 'D MMMM YYYY')}
                />
              )}

              {/* Resend relationship banners */}
              {response.parent_order && (
                <div
                  className='rounded-2xl border border-solid p-4 mb-4 flex items-center gap-3'
                  style={{
                    borderColor: 'rgba(59,130,246,0.2)',
                    backgroundColor: 'rgba(59,130,246,0.05)',
                  }}
                >
                  <RotateCw size={16} className='shrink-0' style={{ color: 'rgb(37,99,235)' }} />
                  <span className='text-sm' style={{ color: 'rgb(30,64,175)' }}>
                    {String(t('resend_of_order'))}{' '}
                    <button
                      onClick={() => navigate(`/orders/${response.parent_order!.uid}`)}
                      className='font-semibold hover:underline cursor-pointer bg-transparent border-0 p-0'
                      style={{ color: 'rgb(37,99,235)' }}
                    >
                      {response.parent_order.platform_code}
                    </button>
                  </span>
                </div>
              )}

              {response.resend_orders && response.resend_orders.length > 0 && (
                <div
                  className='rounded-2xl border border-solid p-4 mb-4'
                  style={{
                    borderColor: 'rgba(245,158,11,0.2)',
                    backgroundColor: 'rgba(245,158,11,0.05)',
                  }}
                >
                  <div className='flex items-center gap-3 mb-2'>
                    <RotateCw size={16} className='shrink-0' style={{ color: 'rgb(217,119,6)' }} />
                    <span className='text-sm font-semibold' style={{ color: 'rgb(146,64,14)' }}>
                      {String(t('resend_orders_title'))}
                    </span>
                  </div>
                  {response.resend_orders.map((child) => (
                    <div key={child.uid} className='flex items-center gap-2 ms-7 mt-1'>
                      <span className='text-sm' style={{ color: 'rgb(180,83,9)' }}>
                        →
                      </span>
                      <button
                        onClick={() => navigate(`/orders/${child.uid}`)}
                        className='text-sm font-medium hover:underline cursor-pointer bg-transparent border-0 p-0'
                        style={{ color: 'rgb(180,83,9)' }}
                      >
                        {child.platform_code}
                      </button>
                      <span
                        className='text-xs rounded-full px-2 py-0.5'
                        style={{
                          color: 'rgb(217,119,6)',
                          backgroundColor: 'rgba(245,158,11,0.12)',
                        }}
                      >
                        {(child.status as any)?.merchant_title ||
                          t(`merchant_title_order_status.${child.status?.slug}`, {
                            defaultValue: child.status?.title,
                          })}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Existing claim banner */}
              {response?.claim && hasPermission(PermissionName.VIEW_CLAIMS) && (
                <div
                  className='rounded-2xl border border-solid p-4 mb-4 flex items-center gap-3 cursor-pointer transition-colors'
                  style={{
                    borderColor: 'rgba(59,130,246,0.2)',
                    backgroundColor: 'rgba(59,130,246,0.05)',
                  }}
                  onClick={() => navigate(`/claims/${response.claim!.uid}`)}
                >
                  <ShieldAlert size={16} className='shrink-0' style={{ color: 'rgb(37,99,235)' }} />
                  <span className='text-sm flex-1' style={{ color: 'rgb(30,64,175)' }}>
                    {String(
                      t('claim_order_info', { defaultValue: 'This order has an active claim' }),
                    )}{' '}
                    <span className='font-semibold' style={{ color: 'rgb(37,99,235)' }}>
                      {response.claim!.platform_code}
                    </span>
                  </span>
                  <ClaimStatusTag
                    slug={response.claim!.status?.slug as import('@/types').ClaimStatusSlug}
                  />
                  <ChevronLeft
                    size={16}
                    className='shrink-0 rtl:rotate-0 ltr:rotate-180'
                    style={{ color: 'rgba(59,130,246,0.4)' }}
                  />
                </div>
              )}

              {/* File claim CTA — RTO-delivered/partially orders AND Path A
                  orders (rto-received-warehouse where parcel never reached
                  a carrier — the modal checks eligibility and gates UI).
                  Backend's eligibility endpoint is the authoritative gate;
                  this status list just decides whether to show the CTA. */}
              {!response?.claim &&
                hasPermission(PermissionName.FILE_CLAIM) &&
                (['rto-delivered', 'rto-partially-delivered', 'rto-received-warehouse'].includes(
                  response?.status?.slug || '',
                ) ||
                  // BOXY-501 — after a PARTIAL delivery the rest (lost or damaged
                  // boxes) is claimable at partially-delivered and in inventory.
                  (!!response?.partially_delivered_at &&
                    ['partially-delivered', 'inventory'].includes(
                      response?.status?.slug || '',
                    ))) && (
                  <div
                    className='rounded-2xl border border-solid p-4 mb-4 flex items-center justify-between gap-3 cursor-pointer transition-colors'
                    style={{
                      borderColor: 'rgba(245,158,11,0.2)',
                      backgroundColor: 'rgba(245,158,11,0.05)',
                    }}
                    onClick={() => setClaimModalOpen(true)}
                  >
                    <div className='flex items-center gap-3'>
                      <ShieldAlert
                        size={16}
                        className='shrink-0'
                        style={{ color: 'rgb(217,119,6)' }}
                      />
                      <div className='flex flex-col gap-0.5'>
                        <span className='text-sm font-semibold' style={{ color: 'rgb(146,64,14)' }}>
                          {String(t('file_a_claim', { defaultValue: 'File a Claim' }))}
                        </span>
                        <span className='text-xs' style={{ color: 'rgb(217,119,6)' }}>
                          {String(
                            t('file_claim_description', {
                              defaultValue: 'Report damage or issues with this returned order',
                            }),
                          )}
                        </span>
                      </div>
                    </div>
                    <button
                      type='button'
                      className='flex items-center gap-1.5 h-8 px-4 rounded-lg bg-[rgb(237,224,74)] text-[rgb(57,57,60)] text-[12px] font-semibold cursor-pointer hover:bg-[rgb(230,217,60)] transition-colors uppercase whitespace-nowrap'
                      onClick={(e) => {
                        e.stopPropagation();
                        setClaimModalOpen(true);
                      }}
                    >
                      {String(t('file_claim_btn', { defaultValue: 'File Claim' }))}
                    </button>
                  </div>
                )}

              {/* DETAILS TAB */}
              {activeSegment === 'details' && (
                <div className='grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-5 items-start'>
                  {/* LEFT COLUMN */}
                  <div className='flex flex-col gap-5' ref={leftSectionRef}>
                    {/* Driver Info — shown for RTO orders */}
                    {activeTab === OrderStatusTab.RTO && response.driver && (
                      <div className='rounded-2xl border border-sd-border bg-sd-card p-5'>
                        <DriverInfo assign driver={response.driver} />
                      </div>
                    )}

                    {/* Service + Order Info side by side; no service card for a
                        merchant without a plan (setting.uses_services=false). */}
                    <div
                      className={`grid grid-cols-1 gap-5 ${usesServices ? 'sm:grid-cols-2' : ''}`}
                    >
                      <CarrierSection serviceCode={response.service_code ?? null} />
                      <OrderInfoSection
                        customId={response.custom_id}
                        orderType={String(t(getOrderTypeLabelKey(response.type as OrderTypeEnum)))}
                        trackingUrl={response.tracking_page}
                        trackingAuth={response.tracking_auth_page}
                        clueVerificationLink={orderOptions.clueLink}
                        idCheck={orderOptions.isIdChecked}
                        status={response.status}
                        sla={response.sla}
                      />
                    </div>

                    {/* Customer Details */}
                    <CustomerSection
                      name={response.full_name || '—'}
                      phone={response.phone || '—'}
                      province={response.region?.province || ''}
                      area={response.region?.area_name || ''}
                      address={response.address_text || ''}
                      deliveryInstructions={response.description}
                    />

                    {/* Items */}
                    <ItemSection
                      products={
                        response.products?.map((p) => ({
                          title: p.title,
                          quantity:
                            typeof p.quantity === 'string' ? Number(p.quantity) : p.quantity,
                          price: p.price,
                          status: p.status,
                        })) || []
                      }
                      currency={String(t('iqd'))}
                    />

                    {/* Pickup + Package side by side */}
                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-5'>
                      <PickupSection
                        address={
                          getOrderPickupAddress(response as unknown as OrderItem)?.title || '—'
                        }
                      />
                      <PackageSection
                        size={
                          PackageSizeEnum[response.size as PackageSizeFormValues] ||
                          response.size ||
                          '—'
                        }
                        isFragile={!!response.is_fragile}
                        temperatureControlled={!!response.temperature_control}
                        idVerification={orderOptions.isIdChecked}
                      />
                    </div>

                    {/* BOXY-301 — multi-box orders get their own card, one row per box. */}
                    {Array.isArray(response.boxes) && response.boxes.length >= 2 ? (
                      <PackagesSection
                        boxes={response.boxes}
                        statusSlug={response.status?.slug}
                        labelsStale={!!response.labels_stale}
                        onPrintLabels={onSubmit}
                      />
                    ) : null}

                    {/* Tickets — fetches tickets linked to this order by platform code */}
                    {hasPermission(PermissionName.DASHBOARD) && (
                      <TicketsSection platformCode={response.platform_code} />
                    )}

                    {/* Proof of Delivery */}
                    <ProofOfDeliveryCard
                      orderUid={response.uid}
                      enabled={activeTab === OrderStatusTab.FINISHED}
                    />

                    {/* Order Comments */}
                    <OrderComments
                      statusAnnotationsData={statusAnnotationsData?.object?.items ?? []}
                    />

                    {/* Transaction Log */}
                    {hasPermission(PermissionName.FINANCE) && (
                      <TransactionListSection orderId={orderId as string} />
                    )}

                    {/* Activity Log */}
                    <ActivityLogSection
                      items={activityLogItems}
                      isLoading={loadingActivityLog}
                      hasMore={hasMoreActivity}
                      loadingMore={loadingMoreActivity}
                      onLoadMore={loadMoreActivity}
                      archived={activityLogArchived}
                      requested={activityLogRequested}
                      onRequest={() => setActivityLogRequested(true)}
                    />
                  </div>

                  {/* RIGHT COLUMN (sticky) */}
                  <div className='flex flex-col gap-5 xl:sticky xl:top-0'>
                    {(() => {
                      // One set of figures for the cost card: goods, who pays how
                      // much shipping, and what the merchant nets.
                      const productsValue = response.preview?.orders_value || 0;
                      const actualShipping = response.preview?.orders_shipment_fee || 0;
                      const customFee =
                        ((response.preview as any)?.custom_shipment_fee ??
                          response.preview?.orders_custom_shipment_fee) ||
                        0;
                      const largeFee = response.preview?.large_order_fee_iqd ?? 0;
                      const { customerShipping, merchantShipping, customerPays, youReceive } =
                        costAmounts({
                          goods: productsValue,
                          shippingFee: actualShipping,
                          feeCustomerPayable: customFee,
                          byCustomer: response.shipment_fee_type === ShipmentFeeType.BY_CUSTOMER,
                          prepaid: response.payment_type === PaymentType.PREPAID,
                          largeOrderFee: largeFee,
                        });
                      const boxes = (response as any).boxes as
                        | Array<{ sequence: number; size: string; fee?: number; charged?: number }>
                        | undefined;
                      return (
                        <CostSummarySection
                          currency={String(t('iqd'))}
                          products={
                            response.products?.map((p) => ({
                              label: `${p.title ?? String(t('products'))}${p.quantity != null ? ` × ${p.quantity}` : ''}`,
                              amount: Number(p.price) || 0,
                            })) || []
                          }
                          shipping={{
                            customerAmount: customerShipping,
                            merchantAmount: merchantShipping,
                          }}
                          boxes={
                            Array.isArray(boxes) &&
                            boxes.length >= 2 &&
                            boxes.every((box) => typeof box.charged === 'number')
                              ? boxes.map((box) => ({
                                  sequence: box.sequence,
                                  size: box.size,
                                  fee: Number(box.fee ?? box.charged),
                                  charged: Number(box.charged),
                                }))
                              : undefined
                          }
                          largeOrderFee={
                            largeFee
                              ? {
                                  amount: largeFee,
                                  reason: response.preview?.large_order_fee_reason as any,
                                }
                              : undefined
                          }
                          customerPays={customerPays}
                          youReceive={youReceive}
                        />
                      );
                    })()}

                    <PaymentSummarySection
                      paymentMethod={
                        response.payment_type === PaymentType.COLLECT_ON_DELIVERY
                          ? String(t('cod'))
                          : String(t('prepaid'))
                      }
                      orderFee={
                        response.shipment_fee_type === ShipmentFeeType.BY_CUSTOMER
                          ? String(t('by_customer'))
                          : String(t('by_merchant'))
                      }
                      // R54 (CA.1) — this rendered a bare `5000`: no thousand
                      // separator and no currency, alone among every money
                      // figure on the page. Same treatment as "Customer pays"
                      // two rows below it.
                      shippingFee={`${formatWithThousandSeparator(
                        response.preview?.orders_shipment_fee || 0,
                      )} ${String(t('iqd'))}`}
                      largeOrderFee={response.preview?.large_order_fee_iqd}
                      largeOrderFeeReason={response.preview?.large_order_fee_reason}
                      serviceSwitch={response.service_switch}
                      customerPays={(() => {
                        const productsValue = response.preview?.orders_value || 0;
                        const actualShipping = response.preview?.orders_shipment_fee || 0;
                        const customFee =
                          ((response.preview as any)?.custom_shipment_fee ??
                            response.preview?.orders_custom_shipment_fee) ||
                          0;
                        const isPrepaid = response.payment_type === PaymentType.PREPAID;
                        const isByCustomer =
                          response.shipment_fee_type === ShipmentFeeType.BY_CUSTOMER;
                        if (isPrepaid && !isByCustomer) return 0;
                        if (isPrepaid && isByCustomer && customFee > 0) return customFee;
                        if (isPrepaid && isByCustomer) return actualShipping;
                        if (!isPrepaid && isByCustomer && customFee > 0)
                          return productsValue + customFee;
                        if (!isPrepaid && isByCustomer) return productsValue + actualShipping;
                        return productsValue;
                      })()}
                      currency={String(t('iqd'))}
                    />

                    <TrackingTimeline
                      currentStatusSlug={response.status?.slug || ''}
                      timelineEvents={timelineItems?.map((item) => ({
                        title: item.title,
                        date: item.day ? `${item.day} ${item.month}` : undefined,
                      }))}
                    />
                  </div>
                </div>
              )}

              {/* UPDATES TAB — BOXY-49; mounted (and fetched) only once opened. */}
              {activeSegment === 'updates' && updatesTabEnabled && (
                <OrderUpdatesTab
                  orderUid={response.uid}
                  customerPhone={response.phone}
                  canResolve={response.merchant_action_required === 1}
                  // The header shows its own RESOLVE in exactly this case; never two.
                  showResolve={!headerShowsResolve(response)}
                  onResolve={() => setResolveModalOpen(true)}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Resolve Modal */}
      <ResolveModal
        visible={resolveModalOpen}
        onClose={() => setResolveModalOpen(false)}
        orderId={orderId}
        platformCode={response?.platform_code}
        comment={response?.merchant_action_required_text || ''}
        commentDate={formatDateByDayjs(response?.updated_at, 'D MMM YYYY, HH:mm')}
      />

      {/* File Claim Modal */}
      {response?.uid && (
        <FileClaimModal
          visible={claimModalOpen}
          onClose={() => setClaimModalOpen(false)}
          orderUid={response.uid}
          onSuccess={() => {
            setClaimModalOpen(false);
            queryClient.invalidateQueries(['order', orderId]);
            queryClient.invalidateQueries(['claim-eligibility', response.uid]);
          }}
        />
      )}
      <DelayRtoConfirmModal
        open={delayRtoModalOpen}
        orders={delayRtoTargetOrders}
        onClose={() => {
          setDelayRtoModalOpen(false);
          setDelayRtoTargetOrders([]);
        }}
      />
    </PageLayout>
  );
};

export default OrderDetails;
